import React from "react";
import {Slider} from "@mui/material";
import {TuneOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";
import {discountApr} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";

const NET_OPTIONS = [30, 45, 60, 90];

type FieldProps = {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    suffix: string;
    onChange: (v: number) => void;
};

const SliderField: React.FC<FieldProps> = ({label, value, min, max, step, suffix, onChange}) => (
    <label className="ptv2-calc-field">
        <span className="ptv2-calc-label">{label}<b className="ptv2-num">{value}{suffix}</b></span>
        <Slider size="small" value={value} min={min} max={max} step={step} aria-label={label}
                onChange={(_e, v) => onChange(v as number)}/>
    </label>
);

/** Is an early-payment discount worth more than the cash? */
export const PtV2AprCalculator: React.FC = () => {
    const [discount, setDiscount] = React.useState(2);
    const [windowDays, setWindowDays] = React.useState(10);
    const [net, setNet] = React.useState(30);
    const [coc, setCoc] = React.useState(8);
    const apr = discountApr(discount, windowDays, net);
    const take = apr != null && apr * 100 > coc;
    const tone = apr == null ? 'ptv2-faint' : take ? 'ptv2-good' : 'ptv2-warn';

    return <PtV2Card title="Is the discount worth taking?"
                     right={<Tag variant="outlined" color="default" icon={<TuneOutlined/>} label="Your inputs"/>}>
        <div className="ptv2-calc">
            <div className="ptv2-calc-inputs">
                <SliderField label="Cash discount" value={discount} min={0.5} max={4} step={0.5} suffix="%" onChange={setDiscount}/>
                <SliderField label="Discount window" value={windowDays} min={0} max={30} step={1} suffix=" days" onChange={setWindowDays}/>
                <div className="ptv2-calc-field">
                    <span className="ptv2-calc-label">Net term (days)</span>
                    <SegmentedControl labels={NET_OPTIONS.map(String)} value={NET_OPTIONS.indexOf(net)}
                                      onChange={i => setNet(NET_OPTIONS[i])}/>
                </div>
                <SliderField label="Cost of capital (your assumption)" value={coc} min={2} max={20} step={1} suffix="%" onChange={setCoc}/>
            </div>
            <div className="ptv2-calc-result" aria-live="polite">
                <span className="ptv2-eyebrow">Annualised return of paying early</span>
                <span className={'ptv2-calc-value ptv2-num ' + tone}>{apr == null ? '—' : `${(apr * 100).toFixed(1)}%`}</span>
                {apr == null
                    ? <Tag color="default" label="The discount window must end before the net term"/>
                    : take
                        ? <Tag color="success" label={`Pay on day ${windowDays}: beats a ${coc}% cost of capital`}/>
                        : <Tag color="warning" label={`Hold the cash: below a ${coc}% cost of capital`}/>}
                <code className="ptv2-formula">
                    APR = {discount}% ÷ (100% − {discount}%) × 365 ÷ ({net} − {windowDays}) days
                </code>
                <p className="ptv2-muted">
                    On NT60 or NT90 suppliers, accelerate only when the discount is above 2.5–3.0%, to protect working
                    capital. On NT30 suppliers, route 2% terms to a fast-track approval.
                </p>
            </div>
        </div>
    </PtV2Card>;
};
